import { useState, useCallback, useMemo } from "react";

type LocationManagementFilters = {
  searchKey: string;
  groupId: string[];
  participantId: string[];
  pageSize: number;
  pageIndex: number;
  sortBy: string;
  sortByDir: string;
};

type LocationManagementFilterKey = keyof LocationManagementFilters;

type LocationManagementFilterValue =
  LocationManagementFilters[LocationManagementFilterKey];

/**
 * Custom hook to manage location management state
 * Handles filters, pagination, sorting
 */
export const useLocationManagement = (initialPageSize = 10) => {
  const [filters, setFilters] = useState({
    searchKey: "",
    groupId: [],
    participantId: [],
    pageSize: initialPageSize,
    pageIndex: 0,
    sortBy: "",
    sortByDir: "",
  });

  // Handle filter changes (on-change, no Apply button)
  const handleFilterChange = useCallback(
    (
      filterKey: LocationManagementFilterKey,
      filterValue: LocationManagementFilterValue
    ) => {
      setFilters((prev) => {
        if (prev[filterKey] === filterValue && prev.pageIndex === 0)
          return prev;
        return {
          ...prev,
          [filterKey]: filterValue,
          pageIndex: 0, // Reset to first page when filter changes
        };
      });
    },
    []
  );

  // Handle clear all filters
  const handleClearFilters = useCallback(() => {
    setFilters((prev) => ({
      searchKey: "",
      groupId: [],
      participantId: [],
      pageSize: prev.pageSize,
      pageIndex: 0,
      sortBy: "",
      sortByDir: "",
    }));
  }, []);

  // Handle pagination
  const handlePageChange = useCallback((pageIndex: number) => {
    setFilters((prev) => {
      if (prev.pageIndex === pageIndex) return prev;
      return {
        ...prev,
        pageIndex,
      };
    });
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setFilters((prev) => {
      if (prev.pageSize === pageSize && prev.pageIndex === 0) return prev;
      return {
        ...prev,
        pageSize,
        pageIndex: 0, // Reset to first page
      };
    });
  }, []);

  // Handle sorting
  const handleSort = useCallback((sortBy: string, sortByDir: string) => {
    setFilters((prev) => {
      if (prev.sortBy === sortBy && prev.sortByDir === sortByDir) return prev;
      return {
        ...prev,
        sortBy,
        sortByDir,
      };
    });
  }, []);

  // Derive request params for API
  const requestParams = useMemo(
    () => ({
      page: filters.pageIndex + 1, // API expects 1-based page
      row: filters.pageSize,
      searchKey: filters.searchKey,
      groupId: filters.groupId,
      participantId: filters.participantId,
      sortBy: filters.sortBy,
      sortByDir: filters.sortByDir,
    }),
    [filters]
  );

  return {
    filters,
    requestParams,
    handleFilterChange,
    handleClearFilters,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    setFilters,
  };
};
